import {
  faArrowLeftLong,
  faAward,
  faBedPulse,
  faCheckCircle,
  faNewspaper,
  faUserFriends,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import images from "../../assets/images";
import CourseCard from "../../components/cards/CourseCard";
import OwlCarousel from "../../components/carousel";
import Footer from "../../components/footer";
import Header from "../../components/header";
import FitnessSection from "../../components/home/FitnessSection";
import Loader from "../../components/loader";
import CustomModal from "../../components/modal";
import FollowerTab from "../../components/publicProfilleTabs/FollowerTab";
import PostsTab from "../../components/publicProfilleTabs/PostsTab";
import ReviewsTab from "../../components/publicProfilleTabs/ReviewsTab";
import Tabs from "../../components/tabs";
import { useTranslate } from "../../context/TranslationContext";
import useCommunityController from "../../controllers/useGuidelineController";
import { useGetCoursesByCategoryQuery } from "../../redux/Apis/Course";
import {
  useGetGuidelineQuery,
  useUpdateGuidelineMutation,
} from "../../redux/Apis/Guideline";
import {
  useCreateGuidelineActionMutation,
  useGetGuidelineActionQuery,
  useReportPostMutation,
  useUpdateGuidelineActionMutation,
} from "../../redux/Apis/GuidelineAction";
import { useGetReviewsQuery } from "../../redux/Apis/Review";
import { useGetInstructorsQuery } from "../../redux/Apis/User";
import {
  useCreateUserFollowerMutation,
  useGetFollowersByUserIdQuery,
  useUpdateFollowerMutation,
} from "../../redux/Apis/UserFollower";
import { setMetaTags } from "../../utils/helper";

const Profile = () => {
  const lang = useSelector((state) => state.language.lang);
  const { t } = useTranslate("instructorDetails");
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.authSlice);
  const userId = user._id;
  const isOwnProfile = user?._id === userId;
  const postRef = useRef(null);
  const followersRef = useRef(null);
  const reviewRef = useRef(null);
  const isNavigatingBack = useRef(false);
  const [activeTab, setActiveTab] = useState(0);
  const [loadingActions, setLoadingActions] = useState({});
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reportDetails, setReportDetails] = useState("");
  const [reportPostId, setReportPostId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [localFollowingMap, setLocalFollowingMap] = useState(new Map());
  const [deletePostId, setDeletePostId] = useState(null);
  // const [isFollowing, setIsFollowing] = useState(false);
  const TAB_KEYS = {
    POST: 0,
    FOLLOWERS: 1,
    REVIEWS: 2,
    SAVED: 3,
  };
  const { data: userActionsData, refetch: refetchActions } =
    useGetGuidelineActionQuery({ params: { lang } }, { skip: !user });
  const [reportPost, { isLoading: isReporting }] = useReportPostMutation();
  const [createGuidelineAction] = useCreateGuidelineActionMutation();
  const [updateGuidelineAction] = useUpdateGuidelineActionMutation();
  const [updateGuideline] = useUpdateGuidelineMutation();

  const {
    data: userData,
    refetch,
    isLoading: isInstructorLoading,
  } = useGetInstructorsQuery(
    { id: userId, params: { lang } },
    { refetchOnMountOrArgChange: true },
  );

  const {
    data: courseTrackData,
    refetch: refetchCourseTrack,
    isLoading: isCourseTrackLoading,
  } = useGetCoursesByCategoryQuery(
    { created_by: userId, type: "course,track", lang },
    {
      refetchOnReconnect: true,
      refetchOnFocus: true,
    },
  );

  const {
    data: savedPosts,
    isLoading: isLoadingSaved,
    isFetching: isRefetchingSaved,
    refetch: refetchSavedPosts,
  } = useGetGuidelineQuery(
    { params: { userid: userId, type: "save" } },
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const {
    values: { data: communityPosts, isLoading, isRefetching },
    functions: { setDiscussions },
    refetch: refetchPosts,
  } = useCommunityController("list", userId);

  const {
    data: reviewData,
    isLoading: isReviewLoading,
    refetch: refetchReviews,
  } = useGetReviewsQuery({ params: { user: userId } });

  const {
    data: followersData,
    isLoading: isFollowersLoading,
    isError: isFollowersError,
    refetch: refetchFollowers,
  } = useGetFollowersByUserIdQuery(
    { params: { userid: userId } },
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const { data: myFollowingData, refetch: refetchMyFollowing } =
    useGetFollowersByUserIdQuery(
      { params: { created_by: user?._id } },
      {
        skip: !user?._id,
      },
    );

  const [createUserFollower] = useCreateUserFollowerMutation({});

  const [updateFollower] = useUpdateFollowerMutation();

  const myFollowingIds = useMemo(() => {
    return new Set(myFollowingData?.data?.map((item) => item.user_id));
  }, [myFollowingData]);

  const {
    data: digitalProductData,
    refetch: refetchDigitalProduct,
    isLoading: isDigitalProductLoading,
  } = useGetCoursesByCategoryQuery(
    { created_by: userId, type: "digital_product", lang },
    {
      refetchOnReconnect: true,
      refetchOnFocus: true,
    },
  );

  const postCount = useMemo(() => {
    return communityPosts?.length || 0;
  }, [communityPosts]);

  const followersCount = useMemo(() => {
    return followersData?.data?.length || 0;
  }, [followersData]);

  const reviewsCount = useMemo(() => {
    return reviewData?.data?.length || 0;
  }, [reviewData]);

  useEffect(() => {
    setMetaTags(
      "Traineego | Buy or Create Automated Courses & Tracks for Fitness & Wellness",
      "Create, manage, and sell your own courses and tracks, or buy high-quality content. Empower your career through automation and passive income today!",
    );
  }, []);

  useEffect(() => {
    setActiveTab(TAB_KEYS.POST); // reset tab
  }, [userId]);

  useEffect(() => {
    if (myFollowingData?.data) {
      const map = new Map();
      myFollowingData.data.forEach((item) => {
        map.set(item.user_id, item._id);
      });
      setLocalFollowingMap(map);
    }
  }, [myFollowingData]);

  useEffect(() => {
    if (isNavigatingBack.current) return;
    if (activeTab === TAB_KEYS.REVIEWS) {
      refetchReviews();
    }
    if (activeTab === TAB_KEYS.FOLLOWERS) {
      refetchFollowers();
    }
  }, [activeTab]);

  useEffect(() => {
    if (isNavigatingBack.current) return;
    refetchCourseTrack();
    refetchDigitalProduct();
    refetch();
  }, [refetchCourseTrack, refetchDigitalProduct, refetch]);

  const isLiked = (postId) =>
    userActionsData?.data.some(
      (a) => a.guideline === postId && a.type === "like" && a.isactive,
    ) || false;

  const isSaved = (postId) =>
    userActionsData?.data.some(
      (a) => a.guideline === postId && a.type === "save" && a.isactive,
    ) || false;

  const isAdminOrModerator =
    user?.role === "admin" || user?.role === "moderator";

  const bannerPreview = useMemo(() => {
    const banner = userData?.data?.user?.banner_img;

    if (!banner) return null;
    if (banner.startsWith("http")) return banner;
    if (banner.startsWith("images/")) {
      return `${process.env.REACT_APP_S3_BASE_URL}${banner}`;
    }
    return null;
  }, [userData?.data?.user?.banner_img]);

  const logoPreview = useMemo(() => {
    const logo = userData?.data?.user?.logo_img;

    if (!logo) return null;
    if (logo.startsWith("http")) return logo;
    if (logo.startsWith("images/")) {
      return `${process.env.REACT_APP_S3_BASE_URL}${logo}`;
    }
    return null;
  }, [userData?.data?.user?.logo_img]);

  if (isCourseTrackLoading || isDigitalProductLoading || isInstructorLoading) {
    return (
      <p className="text-center">
        <Loader loading={true} />
      </p>
    );
  }

  const courseAndTrackData = courseTrackData?.data || [];
  const digitalProducts = digitalProductData?.data || [];

  const getEmbedUrl = (url) => {
    if (!url) return null;

    const isPlaylist = url.includes("list=");
    const playlistMatch = url.match(/[?&]list=([a-zA-Z0-9_-]+)/);
    const videoMatch = url.match(/(?:v=|\.be\/|\/embed\/)([a-zA-Z0-9_-]{11})/);

    if (isPlaylist && playlistMatch) {
      return `https://www.youtube.com/embed/videoseries?list=${playlistMatch[1]}`;
    }

    if (videoMatch) {
      return `https://www.youtube.com/embed/${videoMatch[1]}`;
    }

    return null;
  };

  const embedUrl = getEmbedUrl(userData?.data?.user?.creator_obj?.youtube);

  const handleFollowerClick = (follower) => {
    if (follower.role === "learner") {
      navigate(`/learner/${follower.created_by}`);
    } else {
      navigate(`/instructor/${follower.created_by}`);
    }
  };

  const handleFollow = async (follower) => {
    if (!user?._id) {
      toast.error(t("toast.loginFirst"));
      return;
    }

    const targetUserId = follower._id || userId || follower.created_by;

    const existingFollowerId = localFollowingMap.get(targetUserId);
    const isCurrentlyFollowing = !!existingFollowerId;

    try {
      // UNFOLLOW
      if (isCurrentlyFollowing) {
        // optimistic
        setLocalFollowingMap((prev) => {
          const newMap = new Map(prev);
          newMap.delete(targetUserId);
          return newMap;
        });

        await updateFollower({
          id: existingFollowerId,
          payload: { follow: false },
          params: {},
        }).unwrap();

        toast.success(t("toast.unfollowSuccess"));
      } else {
        const response = await createUserFollower({
          payload: {
            user: targetUserId,
            created_by: user?._id,
            follow: true,
          },
          params: {},
        }).unwrap();

        const newFollowerId = response?.data?._id;

        // optimistic update with ID
        setLocalFollowingMap((prev) => {
          const newMap = new Map(prev);
          newMap.set(targetUserId, newFollowerId);
          return newMap;
        });

        toast.success(t("toast.followSuccess"));
      }

      // optional sync
      refetchMyFollowing();
      if (activeTab === TAB_KEYS.FOLLOWERS) {
        refetchFollowers();
      }
      refetch();
    } catch (error) {
      console.log("error", error?.message, error?.data?.message);
      toast.error(
        error.message || error.data?.message || "Something went wrong",
      );
    }
  };

  const handleToggleAction = async (postId, type) => {
    if (!user) {
      toast.info(t(`toast.loginTo${type === "like" ? "Like" : "Save"}`));
      return;
    }

    const key = `${postId}-${type}`;
    setLoadingActions((prev) => ({ ...prev, [key]: true }));

    setDiscussions((prev) =>
      prev.map((p) => {
        if (p._id !== postId) return p;
        if (type === "like") {
          const liked = isLiked(postId);
          return { ...p, like: liked ? p.like - 1 : p.like + 1 };
        }
        return p;
      }),
    );

    try {
      const existingAction = userActionsData?.data.find(
        (a) => a.guideline === postId && a.type === type,
      );

      if (existingAction) {
        await updateGuidelineAction({
          id: existingAction._id,
          body: { isactive: !existingAction.isactive },
          params: { lang },
        });
      } else {
        const payload = {
          guideline: postId,
          type,
          result: "true",
        };
        await createGuidelineAction({ payload, params: { lang } });
      }
      await refetchActions();
      await refetchSavedPosts();
    } catch (err) {
      toast.error(
        err?.message || err?.data?.message || `Failed to ${type} post`,
      );
      refetchPosts();
    } finally {
      setLoadingActions((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleEditPost = (postId) => {
    if (!user) {
      toast.info(t("toast.loginToEdit"));
      return;
    }
    window.location.href = `/community/edit/${postId}`;
  };

  const handleDeletePost = (postId) => {
    if (!user) {
      toast.info(t("toast.loginToDelete"));
      return;
    }
    setDeletePostId(postId);
    setShowDeleteModal(true);
  };

  const confirmDeletePost = async () => {
    if (!deletePostId) return;

    const key = `${deletePostId}-delete`;
    setLoadingActions((prev) => ({ ...prev, [key]: true }));
    setShowDeleteModal(false);

    try {
      await updateGuideline({
        id: deletePostId,
        body: { isactive: false },
        params: { lang },
      }).unwrap();
      toast.success(t("toast.postDeleted"));
      await refetchPosts();
    } catch (err) {
      toast.error(
        err?.data?.message || err?.message || "Failed to delete post",
      );
    } finally {
      setLoadingActions((prev) => ({ ...prev, [key]: false }));
      setDeletePostId(null);
    }
  };

  const handleReportPost = (postId) => {
    if (!user) {
      toast.info(t("toast.loginToReport"));
      return;
    }
    setReportPostId(postId);
    setShowReportModal(true);
  };

  const submitReport = async () => {
    if (!reportReason.trim()) {
      toast.info(t("toast.selectReason"));
      return;
    }

    try {
      const body = {
        guideline: reportPostId,
        reason: reportReason,
        details: reportDetails || "",
      };
      const response = await reportPost({ body, params: { lang } }).unwrap();
      toast.success(
        response.message ||
          response.data.message ||
          "Report submitted. Thank you!",
      );
      setShowReportModal(false);
      setReportReason("");
      setReportDetails("");
    } catch (err) {
      toast.error(err?.data?.message || err.message || "Failed to report post");
    }
  };

  const handlePostCopyLink = (postId) => {
    const postUrl = `${window.location.origin}/community/${postId}`;
    navigator.clipboard
      .writeText(postUrl)
      .then(() => {
        toast.success(t("toast.copySuccess"));
      })
      .catch(() => {
        toast.error(t("toast.copyError"));
      });
  };

  return (
    <>
      <Header customLogo={logoPreview} />
      <div className="min-vh-100" style={{ padding: "150px 0 90px 0" }}>
        {/* Hero Section */}
        <section
          className="position-relative"
          style={{
            backgroundColor: "#6f42c1",
            height: "250px",
          }}
        >
          {/* Background Letter */}
          {bannerPreview ? (
            <img
              src={bannerPreview}
              alt="Banner"
              className="position-absolute w-100 h-100"
              style={{
                objectFit: "cover",
                top: 0,
                left: 0,
              }}
            />
          ) : (
            <div
              className="text-white fw-bold position-absolute w-100 text-center"
              style={{
                fontSize: "10rem",
                opacity: 0.15,
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                pointerEvents: "none",
              }}
            >
              {userData?.data?.user?.first_name?.charAt(0)}
            </div>
          )}

          {/* Tagline */}
          <div
            className="position-absolute text-white fw-bold text-end"
            style={{
              bottom: "18px",
              right: "20px",
              fontSize: "1.2rem",
            }}
          >
            {/* Mobile: wrap (with narrow width) */}
            <span
              className="d-block d-sm-none"
              style={{
                maxWidth: "150px",
                whiteSpace: "normal",
                display: "inline-block",
              }}
            >
              {userData?.data?.user?.tagline || t("bannerHeadline")}
            </span>

            {/* Tablet & Desktop: single line, full width */}
            <span
              className="d-none d-sm-block"
              style={{ whiteSpace: "nowrap" }}
            >
              {userData?.data?.user?.tagline || t("bannerHeadline")}
            </span>
          </div>
        </section>

        {/* Profile Section */}
        <div
          className="container position-relative"
          style={{ marginTop: "-70px" }}
        >
          <div className="row align-items-center g-4">
            {/* Profile Section */}
            <div className="col-12 col-md-3 text-center">
              <img
                src={
                  userData?.data?.user?.picture || images.profile.placeholder
                }
                alt="Profile"
                className="img-fluid rounded-circle"
                style={{
                  width: "140px",
                  height: "140px",
                  objectFit: "cover",
                }}
              />

              <h5 className="fw-bold mt-3 text-break">
                {userData?.data?.user?.first_name}{" "}
                {userData?.data?.user?.last_name}
              </h5>

              <p
                onClick={() => navigate("/profile/edit")}
                style={{
                  cursor: "pointer",
                  color: "var(--primaryColor)",
                  fontWeight: "600",
                  textDecorationColor: "var(--primaryColor)",
                  marginTop: "8px",
                  transition: "color 0.3s",
                }}
                onMouseEnter={(e) => (e.target.style.color = "#bb86fc")}
                onMouseLeave={(e) =>
                  (e.target.style.color = "var(--primaryColor)")
                }
              >
                {t("editLink")}
              </p>
            </div>

            {/* Stats Section */}
            <div className="col-12 col-md-9">
              <div className="row g-3 justify-content-center text-center">
                {/* Posts */}
                <div className="col-12 col-sm-4">
                  <div
                    className="d-flex align-items-center justify-content-center gap-2 site-btn"
                    style={{
                      cursor: "pointer",
                      minWidth: "auto",
                      padding: "6px 30px",
                    }}
                    onClick={() =>
                      navigate("/community", {
                        state: userId ? { userId } : undefined,
                      })
                    }
                  >
                    <p className="mb-0 fs-3">{postCount}</p>
                    <div className="d-flex align-items-center">
                      <FontAwesomeIcon icon={faNewspaper} />
                      <span className="ms-2 text-nowrap">{t("post")}</span>
                    </div>
                  </div>
                </div>

                {/* Followers */}
                <div className="col-6 col-sm-4">
                  <div
                    className="d-flex align-items-center justify-content-center gap-2 site-btn"
                    style={{
                      cursor: "pointer",
                      minWidth: "auto",
                      padding: "6px 30px",
                    }}
                    onClick={() => {
                      if (isNavigatingBack.current) return;

                      setActiveTab(TAB_KEYS.FOLLOWERS);

                      setTimeout(() => {
                        if (isNavigatingBack.current) return;

                        followersRef.current?.scrollIntoView({
                          behavior: "smooth",
                        });
                      }, 100);
                    }}
                  >
                    <p className="mb-0 fs-3">{followersCount}</p>
                    <div className="d-flex align-items-center">
                      <FontAwesomeIcon icon={faUserFriends} />
                      <span className="ms-1 text-truncate">
                        {t("followers")}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Reviews */}
                <div className="col-6 col-sm-4">
                  <div
                    className="d-flex align-items-center justify-content-center gap-2 site-btn"
                    style={{
                      cursor: "pointer",
                      minWidth: "auto",
                      padding: "6px 30px",
                    }}
                    onClick={() => {
                      if (isNavigatingBack.current) return;

                      setActiveTab(TAB_KEYS.REVIEWS);

                      setTimeout(() => {
                        if (isNavigatingBack.current) return;

                        reviewRef.current?.scrollIntoView({
                          behavior: "smooth",
                        });
                      }, 100);
                    }}
                  >
                    <p className="mb-0 fs-3">{reviewsCount}</p>
                    <div className="d-flex align-items-center">
                      <FontAwesomeIcon icon={faAward} />
                      <span className="ms-1 text-truncate">{t("reviews")}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* About Section */}
        <div className="container py-5">
          <p>{userData?.data?.user?.bio}</p>

          {/* Social Links */}
          <div className="d-flex gap-3 mt-3">
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={userData?.data?.user?.creator_obj?.instagram}
            >
              <i className="fab fa-instagram fa-lg text-light"></i>
            </a>
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={userData?.data?.user?.creator_obj?.tiktok}
            >
              <i className="fab fa-tiktok fa-lg text-light"></i>
            </a>
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={userData?.data?.user?.creator_obj?.youtube}
            >
              <i className="fab fa-youtube fa-lg text-light"></i>
            </a>
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={userData?.data?.user?.creator_obj?.website}
            >
              <i className="fas fa-globe fa-lg text-light"></i>
            </a>
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={userData?.data?.user?.creator_obj?.facebook}
            >
              <i className="fab fa-facebook fa-lg text-light"></i>
            </a>
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={userData?.data?.user?.creator_obj?.linkedin}
            >
              <i className="fab fa-linkedin fa-lg text-light"></i>
            </a>
          </div>
        </div>

        <div className="container px-4">
          <Tabs
            tabs={[
              { label: t("post") },
              { label: t("followers") },
              { label: t("reviews") },
              ...(isOwnProfile ? [{ label: t("saved") }] : []),
            ]}
            active={activeTab}
            setActive={setActiveTab}
          />
        </div>

        {activeTab === TAB_KEYS.POST && (
          <div ref={postRef} className="container py-4">
            <PostsTab
              posts={communityPosts}
              isLoading={isLoading}
              isRefetching={isRefetching}
              user={user}
              navigate={navigate}
              loadingActions={loadingActions}
              handleToggleAction={handleToggleAction}
              isLiked={isLiked}
              isSaved={isSaved}
              handleEditPost={handleEditPost}
              handleDeletePost={handleDeletePost}
              handleReportPost={handleReportPost}
              handleCopyLink={handlePostCopyLink}
              isAdminOrModerator={isAdminOrModerator}
              t={t}
            />
          </div>
        )}

        {activeTab === TAB_KEYS.REVIEWS && (
          <div ref={reviewRef} className="container py-4">
            <ReviewsTab
              reviewData={reviewData}
              isReviewLoading={isReviewLoading}
              t={t}
            />
          </div>
        )}

        {activeTab === TAB_KEYS.FOLLOWERS && (
          <div ref={followersRef} className="container py-4">
            <FollowerTab
              followersData={followersData}
              isFollowersLoading={isFollowersLoading}
              isFollowersError={isFollowersError}
              handleFollowerClick={handleFollowerClick}
              handleFollow={handleFollow}
              myFollowingIds={myFollowingIds}
              localFollowingIds={localFollowingMap}
              user={user}
              t={t}
            />
          </div>
        )}

        {isOwnProfile && activeTab === TAB_KEYS.SAVED && (
          <div className="container py-4">
            <PostsTab
              posts={savedPosts?.data || []}
              isLoading={isLoadingSaved}
              isRefetching={isRefetchingSaved}
              user={user}
              navigate={navigate}
              loadingActions={loadingActions}
              handleToggleAction={handleToggleAction}
              isLiked={isLiked}
              isSaved={isSaved}
              handleEditPost={handleEditPost}
              handleDeletePost={handleDeletePost}
              handleReportPost={handleReportPost}
              handleCopyLink={handlePostCopyLink}
              isAdminOrModerator={isAdminOrModerator}
              t={t}
            />
          </div>
        )}

        {/* Courses & Tracks */}
        <div className="container py-4">
          <h3 className="fw-bold mb-4 text-center">{t("courseTitle")}</h3>
          {courseAndTrackData && courseAndTrackData.length > 0 ? (
            courseAndTrackData.map((item) => (
              <section className="classes-for-you">
                <div className="container">
                  <div className="row mb-1">
                    <div className="col-12 d-flex align-items-end gap-2 flex-wrap">
                      <h4 className="text-uppercase mb-1">{item?.name}</h4>
                      <p className="mb-0 m-purple-text">
                        {item?.count} classes
                      </p>
                    </div>
                  </div>
                  <div className="row">
                    <OwlCarousel
                      className="category-carousel"
                      options={{
                        margin: 10,
                        loop: false,
                        nav: true,
                        dots: false,
                        lazyLoad: true,
                        autoplay: true,
                        autoplayTimeout: 5000,
                        autoplayHoverPause: true,
                        smartSpeed: 500,
                        navText: [
                          '<i class="fas fa-angle-left"></i>',
                          '<i class="fas fa-angle-right"></i>',
                        ],
                        responsive: {
                          0: { items: 2, stagePadding: 0, autoplay: false },
                          576: {
                            items: 2,
                            stagePadding: 20,
                            autoplay: faBedPulse,
                          },
                          768: {
                            items: 3,
                            stagePadding: 0,
                            autoplay: true,
                          },
                          992: {
                            items: 4,
                            stagePadding: 0,
                            autoplay: true,
                          },
                          1200: {
                            items: 5,
                            stagePadding: 0,
                            autoplay: true,
                          },
                        },
                      }}
                    >
                      {item?.courses?.map((item) => (
                        <div className="">
                          <CourseCard data={item} />
                        </div>
                      ))}
                    </OwlCarousel>
                  </div>
                </div>
              </section>
            ))
          ) : (
            <div
              className="text-center py-4 rounded-2"
              style={{ backgroundColor: "var(--grayColor)" }}
            >
              <p className="text-light fw-bold mb-0 p-md">{t("notCourse")}</p>
            </div>
          )}
        </div>

        {/* My Digital Products */}
        <div className="container py-4">
          <h3 className="fw-bold mb-4 text-center">{t("productTitle")}</h3>
          {digitalProducts && digitalProducts.length > 0 ? (
            digitalProducts.map((item) => (
              <section className="classes-for-you">
                <div className="container">
                  <div className="row mb-1">
                    <div className="col-12 d-flex align-items-end gap-2 flex-wrap">
                      <h4 className="text-uppercase mb-1">{item?.name}</h4>
                      <p className="mb-0 m-purple-text">
                        {item?.count} classes
                      </p>
                    </div>
                  </div>
                  <div className="row">
                    <OwlCarousel
                      className="category-carousel"
                      options={{
                        margin: 10,
                        loop: false,
                        nav: true,
                        dots: false,
                        lazyLoad: true,
                        autoplay: true,
                        autoplayTimeout: 5000,
                        autoplayHoverPause: true,
                        smartSpeed: 500,
                        navText: [
                          '<i class="fas fa-angle-left"></i>',
                          '<i class="fas fa-angle-right"></i>',
                        ],
                        responsive: {
                          0: { items: 2, stagePadding: 0, autoplay: false },
                          576: {
                            items: 2,
                            stagePadding: 20,
                            autoplay: faBedPulse,
                          },
                          768: {
                            items: 3,
                            stagePadding: 0,
                            autoplay: true,
                          },
                          992: {
                            items: 4,
                            stagePadding: 0,
                            autoplay: true,
                          },
                          1200: {
                            items: 5,
                            stagePadding: 0,
                            autoplay: true,
                          },
                        },
                      }}
                    >
                      {item?.courses?.map((item) => (
                        <div className="">
                          <CourseCard data={item} />
                        </div>
                      ))}
                    </OwlCarousel>
                  </div>
                </div>
              </section>
            ))
          ) : (
            <div
              className="text-center py-4 rounded-2"
              style={{ backgroundColor: "var(--grayColor)" }}
            >
              <p className="text-light fw-bold mb-0 p-md">{t("notProduct")}</p>
            </div>
          )}
        </div>

        {/* Success Stories & Work With Me */}
        <div className="container py-4">
          <div className="row align-items-center">
            {/* Success Stories */}
            <div className="col-lg-6 col-md-12 mb-4 text-center">
              <h3 className="fw-bold mb-4 text-uppercase">
                {t("successStory")}
              </h3>

              {/* Responsive YouTube Embed */}
              {embedUrl ? (
                <div className="ratio ratio-16x9 shadow">
                  <iframe
                    src={embedUrl}
                    title="Success Story Video"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    style={{ borderRadius: "12px" }}
                  ></iframe>
                </div>
              ) : (
                <>
                  <p className="text-center text-secondary fw-medium">
                    {t("noSuccessStory_before")} <strong>YouTube</strong>{" "}
                    {t("or")} <strong>TikTok</strong>{" "}
                    {t("noSuccessStory_after")}
                  </p>
                </>
              )}
            </div>

            {/* Work With Me */}
            <div className="col-lg-6 col-md-12 mb-4 text-center">
              <h3 className="fw-bold mb-4 text-uppercase">{t("workWith")}</h3>
              <div className="d-flex flex-column align-items-center gap-3">
                {userData?.data?.user?.creator_obj?.work_with_me_links?.map(
                  (link) => {
                    const url = link.url?.toLowerCase() || "";

                    const getIconForUrl = (url) => {
                      if (/zoom\.us/.test(url)) return "fas fa-video";
                      if (/meet\.google\.com/.test(url)) return "fab fa-google";
                      if (/eventbrite/.test(url)) return "fas fa-calendar";
                      if (/classpass/i.test(url)) return "fas fa-users";
                      if (/mindbodyonline/i.test(url)) return "fas fa-dumbbell";
                      if (/calendly/i.test(url)) return "fas fa-calendar-alt";
                      if (/acuityscheduling/i.test(url))
                        return "fas fa-calendar-check";
                      if (/whatsapp/i.test(url)) return "fab fa-whatsapp";
                      if (
                        /^mailto:/.test(url) ||
                        /^[\w.-]+@[\w.-]+\.\w{2,}$/.test(url)
                      )
                        return "fas fa-envelope";
                      if (/shopify/i.test(url)) return "fas fa-shopping-bag";
                      if (/youtube\.com/.test(url)) return "fab fa-youtube";
                      if (/instagram\.com/.test(url)) return "fab fa-instagram";
                      if (/tiktok\.com/.test(url)) return "fab fa-tiktok";
                      if (/^https?:\/\//.test(url)) return "fas fa-globe";
                      return "fas fa-link";
                    };

                    return (
                      <a
                        key={link._id}
                        href={
                          url.startsWith("http") || url.startsWith("mailto:")
                            ? link.url
                            : `mailto:${link.url}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-decoration-none text-dark"
                      >
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <span
                            className="d-inline-flex justify-content-center align-items-center rounded-circle"
                            style={{
                              width: "50px",
                              height: "50px",
                              background:
                                "linear-gradient(135deg, #ff0080, #7928ca)",
                              color: "white",
                              fontSize: "1.5rem",
                            }}
                          >
                            <i className={getIconForUrl(url)}></i>
                          </span>
                          <span
                            className="fw-bold text-light"
                            style={{ fontSize: "1.2rem" }}
                          >
                            {link.label}
                          </span>
                        </div>
                      </a>
                    );
                  },
                )}
              </div>

              <p className="text-secondary fw-medium mt-3">
                <span className="fw-bold">“{t("workWith")}”</span>{" "}
                {t("noWorkWithMe")}
              </p>
            </div>
          </div>
        </div>

        <div className="container py-4 text-center">
          <p
            className="text-secondary fw-medium mt-3 mx-auto"
            style={{ maxWidth: "500px" }}
          >
            {t("offeredServices")}. <strong>Traineego</strong>{" "}
            {t("notResponsible")}
          </p>
        </div>

        <FitnessSection />
      </div>
      <div className="sticky-action-wrapper">
        <div className="sticky-action-bar">
          <button
            className="action-btn"
            onClick={(e) => {
              navigate(`/instructor/${user._id}`);
            }}
          >
            <FontAwesomeIcon icon={faArrowLeftLong} />
            <span>{t("back")}</span>
          </button>

          <button className="action-btn" onClick={() => navigate("/community")}>
            <FontAwesomeIcon icon={faCheckCircle} />
            <span>{t("feed")}</span>
          </button>
          {/* <button className="action-btn" onClick={handleCopyLink}>
            <FontAwesomeIcon icon={faLink} />
            <span>{t("copyText")}</span>
          </button> */}
        </div>
      </div>
      <Modal
        show={showReportModal}
        onHide={() => setShowReportModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="text-dark">{t("reportPost")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label className="text-dark">
                {t("reasonForReporting")}
              </Form.Label>
              <Form.Control
                as="select"
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
              >
                <option value="">{t("selectReason")}</option>
                <option value="spam">{t("spam")}</option>
                <option value="inappropriatecontent">
                  {t("inappropriateContent")}
                </option>
                <option value="harassment">{t("harassment")}</option>
                <option value="other">{t("other")}</option>
              </Form.Control>
            </Form.Group>

            {reportReason === "other" && (
              <Form.Group>
                <Form.Label className="text-dark">
                  {t("additionalDetails")}
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder={t("describeIssue")}
                  value={reportDetails}
                  onChange={(e) => setReportDetails(e.target.value)}
                />
              </Form.Group>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowReportModal(false)}>
            {t("cancel")}
          </Button>
          <Button
            variant="danger"
            onClick={submitReport}
            disabled={isReporting}
          >
            {isReporting ? t("reporting") : t("submitReport")}
          </Button>
        </Modal.Footer>
      </Modal>
      <CustomModal
        show={showDeleteModal}
        close={() => setShowDeleteModal(false)}
        heading={t("confirmDelete")}
        action={confirmDeletePost}
        loading={loadingActions[`${deletePostId}-delete`]}
      />
      <Footer />
    </>
  );
};

export default Profile;
